// S3 — the fleet overview, the console's home (spec 15 §4; D-625).
//
// One read (`GET /v1/platform/fleet/overview`) gives the page its three parts:
// the KPI tiles, the tenants that need someone to look at them, and the
// fleet's newest lifecycle changes. The tiles are whatever the server's
// sources returned for this role, in the server's order; a tile whose store
// does not exist yet has no source and is not drawn (D-033). Labels and links
// come from fleet/fleet-registry.ts. The page reads again every minute and on
// Refresh, and says when it last read.

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { CardSkeleton, ErrorState } from "../../../components/states";
import { StatStrip, StatTile } from "../../../components/stat-strip";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { enumLabel } from "../../../lib/enum-label";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { FLEET_ATTENTION_VIEWS, FLEET_TILE_VIEWS } from "../fleet/fleet-registry";
import { useActorName } from "../hooks/use-actor-name";
import {
  fetchFleetOverview,
  type FleetAttentionItem,
  type FleetEvent,
  type FleetTile,
  type PlatformMe,
} from "../lib/platform-api";
import { canCreateTenant, statusVariant } from "../lib/tenants";

export const FLEET_REFRESH_MS = 60_000;
export const FLEET_QUERY_KEY = ["platform", "fleet", "overview"] as const;

/** Placeholder tiles while the first read is in flight (reserves the row). */
const SKELETON_TILES = 4;

function TileCard({ tile }: { tile: FleetTile }) {
  const { t, i18n } = useTranslation();
  const view = FLEET_TILE_VIEWS[tile.key];
  const label = view !== undefined ? t(view.labelKey) : tile.key;
  const value = new Intl.NumberFormat(i18n.language).format(tile.value);
  const body = (
    <StatTile label={label} value={value} data-testid={`admin-fleet-tile-${tile.key}`} />
  );
  return (
    <div className="admin-tile" data-tone={tile.tone} data-key={tile.key}>
      {view?.link !== undefined ? (
        <Link
          to={view.link.to}
          {...(view.link.search !== undefined ? { search: view.link.search } : {})}
          className="admin-tile-link"
          aria-label={`${label}: ${value}`}
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </div>
  );
}

function AttentionRow({ item }: { item: FleetAttentionItem }) {
  const { t, i18n } = useTranslation();
  const view = FLEET_ATTENTION_VIEWS[item.kind];
  const link = view?.link?.(item.tenant.id) ?? {
    to: "/console/tenants/$tenantId",
    params: { tenantId: item.tenant.id },
  };
  return (
    <li className="admin-attn-row" data-kind={item.kind} data-testid="admin-fleet-attention-row">
      <StatusChip variant={item.severity === "bad" ? "bad" : "warn"}>
        {view !== undefined ? t(view.labelKey) : item.kind}
      </StatusChip>
      <span className="admin-attn-tenant">
        <Link {...link} className="admin-attn-name" data-testid="admin-fleet-attention-tenant">
          {item.tenant.name}
        </Link>
        <code className="admin-attn-sub">{item.tenant.subdomain}</code>
        {item.tenant.sandbox ? (
          <StatusChip variant="accent" dot={false}>
            {t("admin.tenants.sandbox")}
          </StatusChip>
        ) : null}
      </span>
      <span className="admin-muted admin-attn-since">
        {item.since !== null
          ? t("admin.fleet.since", { when: formatTimestamp(item.since, i18n.language) })
          : null}
      </span>
    </li>
  );
}

function EventRow({ event, actorName }: { event: FleetEvent; actorName: (a: string) => string }) {
  const { t, i18n } = useTranslation();
  const status = (s: string) => enumLabel(t, "admin.status", s);
  const what =
    event.from_status === "" || event.from_status === "none"
      ? t("admin.tenant.createdEvent")
      : `${status(event.from_status)} → ${status(event.to_status)}`;
  return (
    <li className="admin-timeline-row" data-to={event.to_status} data-testid="admin-fleet-event">
      <span className="admin-timeline-what">
        <Link
          to="/console/tenants/$tenantId"
          params={{ tenantId: event.tenant.id }}
          className="admin-attn-name"
        >
          {event.tenant.name}
        </Link>
        <span className="admin-event-what">
          <StatusChip variant={statusVariant(event.to_status)}>{what}</StatusChip>
        </span>
      </span>
      {event.reason !== null && event.reason !== "" ? (
        <span className="admin-muted">{enumLabel(t, "admin.reasons", event.reason)}</span>
      ) : null}
      <span className="admin-muted">
        {formatTimestamp(event.occurred_at, i18n.language)} · {actorName(event.actor)}
      </span>
    </li>
  );
}

export function FleetOverviewScreen({ me }: { me: PlatformMe }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const actorName = useActorName(me.role);
  const overview = useQuery({
    queryKey: FLEET_QUERY_KEY,
    queryFn: ({ signal }) => fetchFleetOverview(signal),
    refetchInterval: FLEET_REFRESH_MS,
  });
  useSignInAgainOn401(overview.error);

  const actions = (
    <span className="admin-head-actions">
      <Button
        size="sm"
        busy={overview.isFetching}
        busyLabel={t("admin.fleet.refreshing")}
        data-testid="admin-fleet-refresh"
        onClick={() => {
          void overview.refetch();
        }}
      >
        {t("admin.fleet.refresh")}
      </Button>
      {canCreateTenant(me.role) ? (
        <Button
          variant="primary"
          size="sm"
          data-testid="admin-fleet-create"
          onClick={() => {
            void navigate({ to: "/console/tenants/new" });
          }}
        >
          {t("admin.tenants.create")}
        </Button>
      ) : null}
    </span>
  );

  const header = (
    <PageHeader
      title={t("admin.fleet.title")}
      actions={actions}
      {...(overview.data !== undefined
        ? {
            meta: t("admin.fleet.updated", {
              when: formatTimestamp(overview.data.generated_at, i18n.language),
            }),
          }
        : {})}
    />
  );

  if (overview.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-fleet-loading">
        {header}
        <StatStrip>
          {Array.from({ length: SKELETON_TILES }, (_, i) => (
            <StatTile key={i} label={t("admin.fleet.loading")} value={undefined} loading />
          ))}
        </StatStrip>
        <CardSkeleton lines={4} label={t("admin.fleet.loading")} />
      </section>
    );
  }

  if (overview.isError) {
    return (
      <section className="admin-screen" data-testid="admin-fleet-error">
        {header}
        <ErrorState
          title={t("admin.load.failed")}
          retryLabel={t("admin.load.retry")}
          onRetry={() => {
            void overview.refetch();
          }}
          requestId={requestIdOf(overview.error)}
          requestIdLabel={t("admin.load.requestId")}
        />
      </section>
    );
  }

  const data = overview.data;
  return (
    <section className="admin-screen" data-testid="admin-fleet">
      {header}

      {data.unavailable.length > 0 ? (
        <Banner kind="warn" data-testid="admin-fleet-unavailable">
          {t("admin.fleet.partial")}
        </Banner>
      ) : null}

      <StatStrip data-testid="admin-fleet-tiles">
        {data.tiles.map((tile) => (
          <TileCard key={tile.key} tile={tile} />
        ))}
      </StatStrip>

      <div className="admin-fleet-grid">
        <Card
          title={t("admin.fleet.attentionTitle")}
          count={data.attention_total}
          data-testid="admin-fleet-attention"
        >
          {data.attention.length === 0 ? (
            <p className="admin-fleet-clear" data-testid="admin-fleet-attention-empty">
              {t("admin.fleet.attentionEmpty")}
            </p>
          ) : (
            <>
              <ul className="admin-attn">
                {data.attention.map((item) => (
                  <AttentionRow key={`${item.kind}:${item.tenant.id}`} item={item} />
                ))}
              </ul>
              {data.attention_total > data.attention.length ? (
                <p className="admin-muted admin-attn-more" data-testid="admin-fleet-attention-more">
                  {t("admin.fleet.attentionMore", {
                    shown: data.attention.length,
                    total: data.attention_total,
                  })}
                </p>
              ) : null}
            </>
          )}
        </Card>

        <Card title={t("admin.fleet.recentTitle")} data-testid="admin-fleet-recent">
          {data.recent_events.length === 0 ? (
            <p className="admin-fleet-clear">{t("admin.fleet.recentEmpty")}</p>
          ) : (
            <ol className="admin-timeline">
              {data.recent_events.map((event) => (
                <EventRow key={event.id} event={event} actorName={actorName} />
              ))}
            </ol>
          )}
        </Card>
      </div>
    </section>
  );
}
